'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * The user-facing wording of the outcomes. Typed as a total `Record` over the
 * shared union on purpose: when the vocabulary grows, `tsc` names this map
 * instead of the UI silently falling back to a raw enum value. (It did exactly
 * that when COP-138 added `unknown`.)
 *
 * `unknown` reads "Unconfirmed", not "Unknown". The row describes an action
 * that may well have gone through and that nobody has checked yet — which is
 * what "unconfirmed" says. "Unknown" reads as a malfunction and pushes the user
 * toward re-running, the one move that can duplicate a write (COP-138).
 */
const OUTCOME_LABEL = {
    running: 'Running',
    awaiting_approval: 'Waiting for you',
    denied: 'Denied',
    succeeded: 'Succeeded',
    failed: 'Failed',
    not_run: 'Not run',
    unknown: 'Unconfirmed',
    // COP-143 — "you sent it back", not "you refused". `denied` named a decision
    // the user did not make; this is the word for the one they did.
    edited: 'Edited',
};
/**
 * An outcome the closed vocabulary does not cover renders VERBATIM rather than
 * being dropped or relabelled: an under-styled honest row beats a missing one
 * (COP-135 Consequences).
 *
 * Exported because the plan card and the schedule preview card word the SAME
 * ledger rows on their own surfaces (task-08). A second label table there would
 * let one record read "Failed" in the action list and something else on the
 * card — the drift `OUTCOME_LABEL` is single-homed to prevent.
 */
export function turnActionOutcomeLabel(outcome) {
    return OUTCOME_LABEL[outcome] ?? outcome;
}
/**
 * Every `data-turn-actions` snapshot on ONE message, in arrival order — the
 * ONE reader of the server-sent part.
 *
 * A message routinely carries SEVERAL: the live seam re-reads the ledger and
 * emits the whole list after every state change (COP-135 D3), and a decision
 * turn adds a snapshot addressed to the turn it settled. They are kept apart
 * here and reconciled by `routeTurnActions`, which is the only place that
 * decides which of them a message shows.
 */
function readSnapshots(parts) {
    if (!Array.isArray(parts))
        return [];
    const snapshots = [];
    for (const part of parts) {
        const candidate = part;
        if (candidate?.type !== 'data-turn-actions')
            continue;
        const turnId = candidate.data?.turnId;
        if (typeof turnId !== 'string' || turnId.length === 0)
            continue;
        const list = candidate.data?.actions;
        snapshots.push({
            turnId,
            actions: Array.isArray(list) ? list : [],
        });
    }
    return snapshots;
}
/**
 * Route every turn's LATEST snapshot to the message that owns that turn.
 *
 * Returns one entry per message, positionally — index `i` holds the snapshots
 * message `i` renders, in the order those turns first appeared. A message that
 * owns no turn gets an empty array; a message that carries only a turn an
 * EARLIER message already owns gets nothing for it, because the earlier message
 * is the one the server addressed.
 *
 * See the module docblock for why the routing key is the server's and not the
 * client's opinion.
 */
export function routeTurnActions(messages) {
    /** turnId → the index of the message that owns it (first one to carry it). */
    const ownerOf = new Map();
    /** turnId → the newest list seen for it, anywhere in the conversation. */
    const latestOf = new Map();
    /** Per message, the turns it owns, in first-appearance order. */
    const owned = messages.map(() => []);
    messages.forEach((message, index) => {
        const parts = message?.parts;
        for (const snapshot of readSnapshots(parts)) {
            if (!ownerOf.has(snapshot.turnId)) {
                ownerOf.set(snapshot.turnId, index);
                owned[index].push(snapshot.turnId);
            }
            latestOf.set(snapshot.turnId, snapshot.actions);
        }
    });
    return owned.map((turnIds) => turnIds.map((turnId) => ({ turnId, actions: latestOf.get(turnId) ?? [] })));
}
/**
 * Whether the routed snapshots carry any row at all.
 *
 * The two bubble-suppression guards read this: a message whose only visible
 * record is its action list must NOT be dropped, or the copilot silently
 * discards actions it took (COP-135 D1).
 */
export function hasTurnActionRows(snapshots) {
    return snapshots.some((snapshot) => snapshot.actions.length > 0);
}
export function indexTurnActions(routed) {
    const index = new Map();
    for (const snapshots of routed) {
        for (const snapshot of snapshots) {
            for (const action of snapshot.actions)
                index.set(action.actionId, action);
        }
    }
    return index;
}
/**
 * `plan:<planId>:<stepIndex>` — the ledger key of ONE plan step (COP-135 D6).
 *
 * Mirrors `planStepActionKey` in `packages/hub/src/chat/turn-actions/cards.ts`,
 * which the executor writes with the step's 1-BASED `step.index` — the same
 * index the stored plan preview carries. The join is therefore an exact key
 * lookup, not an inference: the client never decides what a step did, it only
 * asks the ledger for the row that names it (COP-135 D1).
 */
export function planStepActionId(planId, stepIndex) {
    return `plan:${planId}:${stepIndex}`;
}
/**
 * Whether a row shows its secondary `detail` line.
 *
 * `detail` is a SHORT TYPED CODE (TACT-08, HUB-024) and renders VERBATIM — a
 * user sees the literal `execution_interrupted`, not a sentence. That is a
 * tolerable trade on a `failed` row, where the code narrows down a fault the
 * user is already looking at.
 *
 * It is NOT tolerable on `unknown` (COP-138). That row's whole job is to send
 * the user off to check whether something applied, and the approval card
 * carries the sentence that explains it. A machine code stapled underneath adds
 * no information and makes a row about the user's next action read like a stack
 * trace. Suppressed here — at the render, not at the writer — so the code stays
 * in the ledger for forensics.
 */
function showsDetail(action) {
    if (action.outcome === 'unknown')
        return false;
    return Boolean(action.detail);
}
/**
 * Render one row per action of every turn this message owns: name, outcome,
 * and — when the server sent one and the outcome shows it — the short typed
 * `detail` code as secondary text.
 *
 * Each turn keeps its own list (`data-turn-id`); the lists are never merged.
 * Renders `null` (no element at all) when there are no rows.
 */
export function CopilotTurnActions({ snapshots, className, ...rest }) {
    if (!hasTurnActionRows(snapshots))
        return null;
    return (_jsx(_Fragment, { children: snapshots
            .filter((snapshot) => snapshot.actions.length > 0)
            .map((snapshot) => (_jsx("div", { className: ['copilot-turn-actions', className].filter(Boolean).join(' '), "data-turn-id": snapshot.turnId, ...rest, children: snapshot.actions.map((action) => (_jsxs("div", { className: "copilot-turn-action", "data-turn-action-outcome": action.outcome, children: [_jsx("span", { className: "copilot-turn-action-name", children: action.name }), _jsx("span", { className: "copilot-turn-action-outcome", children: turnActionOutcomeLabel(action.outcome) }), showsDetail(action) ? (_jsx("span", { className: "copilot-turn-action-detail", children: action.detail })) : null] }, action.actionId))) }, snapshot.turnId))) }));
}
//# sourceMappingURL=turn-actions.js.map